import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import { LayoutDashboard, FolderKanban, Settings as SettingsIcon, LogOut, Languages, Users, Calendar, Share2, Database, X } from "lucide-react";
import CompanySwitcher from "./CompanySwitcher";

export default function Sidebar({ open = false, onClose }) {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();

  const linkBase = "flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition-colors";
  const linkInactive = "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900";
  const linkActive = "bg-zinc-900 text-white";

  const onLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <aside
      className={`w-60 shrink-0 border-r border-zinc-200 bg-white flex flex-col fixed inset-y-0 left-0 z-40 overflow-y-auto transition-transform duration-200 lg:static lg:z-auto lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}
      data-testid="sidebar"
    >
      <div className="px-5 py-5 border-b border-zinc-200 relative">
        <button
          onClick={onClose}
          className="lg:hidden absolute top-4 right-3 p-1.5 rounded-md text-zinc-500 hover:bg-zinc-100"
          aria-label="Fechar menu"
          data-testid="sidebar-close-btn"
        >
          <X size={18} />
        </button>
        <Link to="/dashboard" className="block group" data-testid="sidebar-brand">
          <div className="flex items-baseline gap-2">
            <div className="font-heading text-2xl font-bold tracking-tighter text-zinc-950">Cornerstone</div>
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-cs-accent" />
          </div>
          <div className="cs-label mt-1">{t("by_company")}</div>
        </Link>
        <CompanySwitcher />
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1">
        <NavLink to="/dashboard" end className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkInactive}`} data-testid="nav-dashboard">
          <LayoutDashboard size={16} /> {t("nav_dashboard")}
        </NavLink>
        <NavLink to="/projects" className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkInactive}`} data-testid="nav-projects">
          <FolderKanban size={16} /> {t("nav_projects")}
        </NavLink>
        <NavLink to="/work-program" className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkInactive}`} data-testid="nav-work-program">
          <Calendar size={16} /> {t("nav_work_program")}
        </NavLink>
        <NavLink to="/data" className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkInactive}`} data-testid="nav-data">
          <Database size={16} /> {t("nav_data")}
        </NavLink>
        <NavLink to="/share-links" className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkInactive}`} data-testid="nav-share-links">
          <Share2 size={16} /> {t("nav_share_links")}
        </NavLink>
        <NavLink to="/users" className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkInactive}`} data-testid="nav-users">
          <Users size={16} /> {t("nav_users")}
        </NavLink>
        <NavLink to="/settings" className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkInactive}`} data-testid="nav-settings">
          <SettingsIcon size={16} /> {t("nav_settings")}
        </NavLink>
      </nav>

      <div className="px-3 py-3 border-t border-zinc-200 space-y-3">
        <div className="flex items-center gap-2 px-2">
          <Languages size={14} className="text-zinc-500" />
          <select
            value={lang}
            onChange={(e) => setLang(e.target.value)}
            data-testid="language-select"
            className="text-xs bg-transparent border-0 cursor-pointer text-zinc-700 focus:outline-none focus:ring-0"
          >
            <option value="pt">{t("portuguese")}</option>
            <option value="en">{t("english")}</option>
          </select>
        </div>

        <div className="px-2 py-2 rounded-md bg-zinc-50 border border-zinc-200">
          <div className="text-xs font-semibold text-zinc-900 truncate" data-testid="sidebar-user-name">{user?.name}</div>
          <div className="text-[11px] text-zinc-500 truncate" data-testid="sidebar-user-email">{user?.email}</div>
          <div className="text-[10px] uppercase tracking-wider text-cs-accent mt-1">{user?.role}</div>
        </div>

        <button onClick={onLogout} data-testid="logout-btn" className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md text-zinc-700 hover:bg-zinc-100 transition-colors">
          <LogOut size={14} /> {t("nav_logout")}
        </button>
      </div>
    </aside>
  );
}
